import axios from "axios";
import { getFingerprint } from "./fingerprint";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/v1`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT if present (admin) OR subscription token (public)
// + browser fingerprint for free-tier quota tracking
api.interceptors.request.use(async (config) => {
  const adminToken = localStorage.getItem("lexhab_token");
  const subToken = localStorage.getItem("lexhab_sub_token");
  // Admin JWT takes priority when on /admin/* endpoints
  const isAdmin = (config.url || "").startsWith("/admin");
  if (isAdmin && adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (subToken) {
    config.headers.Authorization = `Bearer ${subToken}`;
  } else if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  }

  // Fingerprint — toujours attaché (backend l ignore pour les abonnés)
  try {
    const fid = await getFingerprint();
    if (fid) config.headers["X-Client-Fingerprint"] = fid;
  } catch (_) {}

  return config;
});

export const publicApi = {
  health: () => api.get("/health"),
  publicStats: () => api.get("/public/stats"),
  chat: (payload) => api.post("/chat", payload),
  feedback: (payload) => api.post("/feedback", payload),
  quota: () => api.get("/quota"),
  plans: () => api.get("/plans"),
  subscribe: (payload) => api.post("/subscribe", payload),
  paymentStatus: (depositId) => api.get(`/payment/status/${depositId}`),
};

export const subscriberApi = {
  login: (email, password) => api.post("/auth/subscriber-login", { email, password }),
  register: (body) => api.post("/auth/subscriber-register", body),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, new_password) => api.post("/auth/reset-password", { token, new_password }),
};

export const adminApi = {
  login: (email, password) => api.post("/admin/login", { email, password }),
  me: () => api.get("/admin/me"),
  updateMe: (body) => api.patch("/admin/me", body),
  changePassword: (current_password, new_password) =>
    api.post("/admin/change-password", { current_password, new_password }),
  forgotPassword: (email, language = "fr") =>
    api.post("/admin/forgot-password", { email, language }),
  resetPassword: (token, new_password) =>
    api.post("/admin/reset-password", { token, new_password }),
  permissions: () => api.get("/admin/permissions"),
  stats: () => api.get("/admin/stats"),
  listDocs: (params = {}) => api.get("/admin/documents", { params }),
  getDoc: (id) => api.get(`/admin/documents/${id}`),
  updateDoc: (id, body) => api.patch(`/admin/documents/${id}`, body),
  deleteDoc: (id) => api.delete(`/admin/documents/${id}`),
  ingestText: (metadata, raw_text) =>
    api.post("/admin/ingest/text", { metadata, raw_text }),
  ingestPdf: (formData) =>
    api.post("/admin/ingest/pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 600000, // 10 minutes pour les fichiers volumineux (50Mo+)
    }),
  search: (body) => api.post("/admin/search", body),
  logs: (limit = 20) => api.get("/admin/logs", { params: { limit } }),
  feedback: (params = {}) => api.get("/admin/feedback", { params }),
  updateFeedback: (id, body) => api.patch(`/admin/feedback/${id}`, body),
  deleteFeedback: (id) => api.delete(`/admin/feedback/${id}`),
  // Agents (users)
  listAgents: () => api.get("/admin/agents"),
  createAgent: (body) => api.post("/admin/agents", body),
  updateAgent: (id, body) => api.patch(`/admin/agents/${id}`, body),
  deleteAgent: (id) => api.delete(`/admin/agents/${id}`),
  // Subscriptions
  listSubscriptions: (params = {}) => api.get("/admin/subscriptions", { params }),
  createManualSub: (body) => api.post("/admin/subscriptions", body),
  updateSub: (id, body) => api.patch(`/admin/subscriptions/${id}`, body),
  revokeSub: (id) => api.delete(`/admin/subscriptions/${id}`),
  // Settings
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (body) => api.put("/admin/settings", body),
  testPawapay: () => api.post("/admin/settings/test-pawapay"),
  testResend: (to) => api.post("/admin/settings/test-resend", { to }),
  // Quotas
  resetTodayQuotas: () => api.delete("/admin/quotas/reset/today"),
  resetAllQuotas: () => api.delete("/admin/quotas/reset"),
  // Subscriber management (superadmin)
  listSubscribers: (params = {}) => api.get("/admin/super/subscribers", { params }),
  getSubscriber: (id) => api.get(`/admin/super/subscribers/${id}`),
  createSubscriber: (body) => api.post("/admin/super/subscribers", body),
  updateSubscriber: (id, body) => api.patch(`/admin/super/subscribers/${id}`, body),
  deleteSubscriber: (id) => api.delete(`/admin/super/subscribers/${id}`),
  bulkAction: (body) => api.post("/admin/super/bulk-action", body),
  // System stats & maintenance
  getSystemStats: () => api.get("/admin/super/stats"),
  securityAudit: (days = 30) => api.get("/admin/super/security/audit", { params: { days } }),
  cleanupData: (target) => api.post("/admin/super/maintenance/cleanup", { target }),
  // Audit
  auditSummary: (limit = 10) => api.get("/admin/audit/summary", { params: { limit } }),
  auditList: (params = {}) => api.get("/admin/audit", { params }),
  // Subscriber password-reset requests
  listPasswordResetRequests: (params = {}) =>
    api.get("/admin/password-reset-requests", { params }),
  generateResetLink: (id) =>
    api.post(`/admin/password-reset-requests/${id}/generate-link`),
};
